import { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { loadAd, loadCatalog, loadContinueWatching, loadFeatured, loadHomeAds, loadPartnerTheme, loadSchedules, loadVodFavorites, loadVodTitle, saveWatchProgress, setVodFavorite, signIn } from './core/api';
import { sendAdEventWithRetry } from './core/adEventSender';
import { mergeCatalog } from './core/catalog';
import { normalizePlaybackMediaType } from './player/mediaType';
import { useRemoteNavigation } from './hooks/useRemoteNavigation';
import type { AdCreative, AdEventType, Category, Channel, FeaturedChannel, FeaturedSettings, HomeAds, PartnerTheme, Screen, Session, VodPlayable, VodProgress, VodTitle } from './types';
import { HomeScreen } from './components/HomeScreen';
import { LoginScreen } from './components/LoginScreen';
import { Sidebar } from './components/Sidebar';
import { StatusScreen } from './components/StatusScreen';
import { VodScreen } from './components/VodScreen';
import { PairingActivationScreen } from './components/PairingActivationScreen';
import { PartnerHubScreen } from './components/PartnerHubScreen';

const SESSION_KEY = 'innova-tv-session';
const PlayerScreen = lazy(() => import('./components/PlayerScreen'));
const VodPlayerScreen = lazy(() => import('./components/VodPlayerScreen'));
const DEFAULT_THEME: PartnerTheme = {
  slug: 'innova', name: 'InnovaTV', short_name: 'INNOVA TV', logo_url: '', login_background_url: '',
  primary_color: '#22a8f3', secondary_color: '#0879d8', login_eyebrow: 'SUA TV GRATUITA',
  login_headline: 'Conteúdo para todos, todos os dias.',
  login_body: 'Canais ao vivo, filmes e séries em uma experiência gratuita financiada por publicidade.',
  sponsor_label: 'EXPERIÊNCIA INNOVATV',
  features: [],
  rails: [],
  events: [],
};
const urlParams = new URLSearchParams(window.location.search);
const PARTNER_HINT = urlParams.get('partner');
const PAIRING_CODE = urlParams.get('pair');

function storedSession(): Session | null {
  try {
    const value = localStorage.getItem(SESSION_KEY);
    if (!value) return null;
    const parsed = JSON.parse(value) as Session;
    const sessionPartner = parsed.partner?.slug || 'innova';
    if (PARTNER_HINT && sessionPartner !== PARTNER_HINT) return null;
    return {
      ...parsed,
      partner: parsed.partner ? {
        ...parsed.partner,
        features: parsed.partner.features || [],
        rails: parsed.partner.rails || [],
        events: parsed.partner.events || [],
      } : null,
    };
  } catch {
    return null;
  }
}

export default function App() {
  const [session, setSession] = useState<Session | null>(() => storedSession());
  const adEventContext = useRef({ token: session?.token || null, generation: 0 });
  useEffect(() => () => { adEventContext.current.generation += 1; }, []);
  const [channels, setChannels] = useState<Channel[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [movies, setMovies] = useState<VodTitle[]>([]);
  const [series, setSeries] = useState<VodTitle[]>([]);
  const [featured, setFeatured] = useState<FeaturedChannel[]>([]);
  const [featuredSettings, setFeaturedSettings] = useState<FeaturedSettings>({
    rotation_seconds: 8, minimum_seconds: 5, maximum_seconds: 30, maximum_channels: 6,
  });
  const [continueWatching, setContinueWatching] = useState<VodProgress[]>([]);
  const [homeAds, setHomeAds] = useState<HomeAds>({});
  const [loginAd, setLoginAd] = useState<AdCreative | null>(null);
  const [partnerHeroAd, setPartnerHeroAd] = useState<AdCreative | null>(null);
  const [screensaverAd, setScreensaverAd] = useState<AdCreative | null>(null);
  const [activityTick, setActivityTick] = useState(0);
  const [favoriteIds, setFavoriteIds] = useState<Set<number>>(() => new Set());
  const [screen, setScreen] = useState<Screen>('home');
  const [playing, setPlaying] = useState<Channel | null>(null);
  const [vodPlaying, setVodPlaying] = useState<VodPlayable | null>(null);
  const [vodDetail, setVodDetail] = useState<VodTitle | null>(null);
  const [vodDetailLoading, setVodDetailLoading] = useState(false);
  const [loading, setLoading] = useState(Boolean(session));
  const [authBusy, setAuthBusy] = useState(false);
  const [authError, setAuthError] = useState('');
  const [catalogError, setCatalogError] = useState('');
  const [publicTheme, setPublicTheme] = useState<PartnerTheme>(DEFAULT_THEME);
  const [themeReady, setThemeReady] = useState(false);

  const requestedThemeSlug = session?.partner?.slug || PARTNER_HINT;
  const sessionToken = session?.token || null;

  useEffect(() => {
    let cancelled = false;
    void loadPartnerTheme(requestedThemeSlug).then(theme => {
      if (cancelled) return;
      setPublicTheme(theme);
      setSession(current => {
        if (!current?.partner || current.partner.slug !== theme.slug) return current;
        const hydrated = { ...current, partner: theme };
        localStorage.setItem(SESSION_KEY, JSON.stringify(hydrated));
        return hydrated;
      });
    }).catch(() => { /* o tema local mantém login e ativação disponíveis */ })
      .finally(() => { if (!cancelled) setThemeReady(true); });
    return () => { cancelled = true; };
  }, [requestedThemeSlug]);

  useEffect(() => {
    if (session) return;
    let cancelled = false;
    void loadAd(null, 'tv_login_banner', requestedThemeSlug).then(ad => {
      if (!cancelled) setLoginAd(ad);
    }).catch(() => {
      if (!cancelled) setLoginAd(null);
    });
    return () => { cancelled = true; };
  }, [requestedThemeSlug, session]);

  const refreshCatalog = useCallback(async () => {
    if (!session) return;
    setLoading(true);
    setCatalogError('');
    try {
      const [catalog, progress] = await Promise.all([
        loadCatalog(), loadContinueWatching(session.token),
      ]);
      setChannels(mergeCatalog(catalog.channels, catalog.schedules));
      setCategories(catalog.categories);
      setMovies(catalog.movies);
      setSeries(catalog.series);
      setContinueWatching(progress);
      const favorites = await loadVodFavorites(session.token);
      setFavoriteIds(new Set(favorites.map(title => title.id)));
    } catch (error) {
      setCatalogError(error instanceof Error ? error.message : 'Erro desconhecido.');
    } finally {
      setLoading(false);
    }
  }, [session]);

  useEffect(() => {
    if (!sessionToken) return;
    let cancelled = false;
    const controller = new AbortController();
    void Promise.all([loadCatalog(), loadContinueWatching(sessionToken)])
      .then(([catalog, progress]) => {
        if (cancelled) return;
        setChannels(mergeCatalog(catalog.channels, catalog.schedules));
        setCategories(catalog.categories);
        setMovies(catalog.movies);
        setSeries(catalog.series);
        setContinueWatching(progress);
      })
      .catch(error => {
        if (!cancelled) setCatalogError(error instanceof Error ? error.message : 'Erro desconhecido.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    void loadHomeAds(sessionToken, { signal: controller.signal }).then(result => {
      if (!cancelled) {
        setHomeAds(result.ads);
        setPartnerHeroAd(result.partnerHero);
      }
    }).catch(() => {
      if (!cancelled) {
        setHomeAds({});
        setPartnerHeroAd(null);
      }
    });
    void loadFeatured().then(result => {
      if (!cancelled) {
        setFeatured(result.items);
        setFeaturedSettings(result.settings);
      }
    }).catch(() => {
      if (!cancelled) setFeatured([]);
    });
    void loadVodFavorites(sessionToken).then(favorites => {
      if (!cancelled) setFavoriteIds(new Set(favorites.map(title => title.id)));
    }).catch(() => {
      if (!cancelled) setFavoriteIds(new Set());
    });
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [sessionToken]);

  const sendAdEvent = useCallback((ad: AdCreative, eventType: AdEventType) => {
    const token = session?.token || null;
    const generation = adEventContext.current.generation;
    void sendAdEventWithRetry(token, ad, eventType, undefined, undefined, {
      isCurrent: () => adEventContext.current.token === token && adEventContext.current.generation === generation,
    }).catch(() => {
      // Publicidade e telemetria nunca podem interromper a experiência principal.
    });
  }, [session]);

  useEffect(() => {
    if (!session || screen !== 'home' || playing || vodPlaying) { setScreensaverAd(null); return; }
    const activity = () => { setScreensaverAd(null); setActivityTick(value => value + 1); };
    ['keydown', 'pointerdown', 'mousemove'].forEach(event => window.addEventListener(event, activity, { passive: true }));
    const timer = window.setTimeout(() => {
      const ad = homeAds.home_screensaver;
      if (ad) { setScreensaverAd(ad); sendAdEvent(ad, 'impression'); }
    }, 300_000);
    return () => {
      window.clearTimeout(timer);
      ['keydown', 'pointerdown', 'mousemove'].forEach(event => window.removeEventListener(event, activity));
    };
  }, [activityTick, homeAds.home_screensaver, playing, screen, sendAdEvent, session, vodPlaying]);

  const selectAd = useCallback((ad: AdCreative) => {
    sendAdEvent(ad, 'click');
    if (ad.destination_kind === 'channel' && ad.destination_id) {
      const destination = channels.find(channel => channel.id === ad.destination_id);
      if (destination) setPlaying(destination);
      return;
    }
    if (ad.destination_kind === 'title' && ad.destination_id) {
      const destination = [...movies, ...series].find(title => title.id === ad.destination_id);
      if (destination) {
        setScreen(destination.kind === 'movie' ? 'movies' : 'series');
        setVodDetailLoading(true);
        void loadVodTitle(destination.id).then(setVodDetail).catch(() => {
          setCatalogError('Não foi possível abrir o conteúdo patrocinado.');
        }).finally(() => setVodDetailLoading(false));
      }
    }
    // Smart TVs não abrem destinos externos: o fluxo deve permanecer no aplicativo.
  }, [channels, movies, sendAdEvent, series]);

  const toggleFavorite = useCallback((title: VodTitle) => {
    if (!session) return;
    const nextFavorite = !favoriteIds.has(title.id);
    setFavoriteIds(current => {
      const next = new Set(current);
      if (nextFavorite) next.add(title.id);
      else next.delete(title.id);
      return next;
    });
    void setVodFavorite(session.token, title.id, nextFavorite).catch(() => {
      setFavoriteIds(current => {
        const reverted = new Set(current);
        if (nextFavorite) reverted.delete(title.id);
        else reverted.add(title.id);
        return reverted;
      });
    });
  }, [favoriteIds, session]);

  useRemoteNavigation(!playing && !vodPlaying && !loading, `${screen}${vodDetail ? '-detail' : ''}`, () => {
    if (vodDetail) setVodDetail(null);
    else if (screen !== 'home') setScreen('home');
  });

  const selectVod = async (title: VodTitle) => {
    setVodDetailLoading(true);
    try { setVodDetail(await loadVodTitle(title.id)); }
    catch (error) { setCatalogError(error instanceof Error ? error.message : 'Não foi possível abrir o título.'); }
    finally { setVodDetailLoading(false); }
  };

  const selectHomeVod = (title: VodTitle) => {
    setVodDetail(null);
    setScreen(title.kind === 'movie' ? 'movies' : 'series');
    void selectVod(title);
  };

  const resumeVod = async (progress: VodProgress) => {
    setVodDetailLoading(true);
    try {
      const detail = await loadVodTitle(progress.title_id);
      if (progress.content_type === 'movie' && detail.stream_url) {
        setVodPlaying({
          id: `movie-${detail.id}`, name: detail.name, description: detail.description,
          artwork: detail.backdrop || detail.poster, stream_url: detail.stream_url,
          mediaType: normalizePlaybackMediaType(detail.stream_type),
          subtitle: progress.subtitle, title_id: detail.id,
          start_seconds: progress.position_seconds,
          ad_markers: detail.ad_markers || [],
        });
      } else {
        const episode = detail.seasons?.flatMap(season => season.episodes)
          .find(item => item.id === progress.episode_id);
        if (!episode) throw new Error('O episódio salvo não está mais disponível.');
        setVodPlaying({
          id: `episode-${episode.id}`, name: episode.name, description: episode.description,
          artwork: episode.poster || detail.backdrop || detail.poster,
          stream_url: episode.stream_url, subtitle: progress.subtitle,
          mediaType: normalizePlaybackMediaType(episode.stream_type),
          title_id: detail.id, episode_id: episode.id,
          start_seconds: progress.position_seconds,
          ad_markers: episode.ad_markers || [],
        });
      }
    } catch (error) {
      setCatalogError(error instanceof Error ? error.message : 'Não foi possível retomar o conteúdo.');
    } finally { setVodDetailLoading(false); }
  };

  const persistProgress = useCallback((item: VodPlayable, position: number, duration: number) => {
    if (!session || (!item.title_id && !item.episode_id)) return;
    void saveWatchProgress(session.token, item, position, duration)
      .then(() => loadContinueWatching(session.token))
      .then(setContinueWatching)
      .catch(() => { /* a reprodução não pode falhar por telemetria de progresso */ });
  }, [session]);

  const closeVod = useCallback(() => setVodPlaying(null), []);

  const acceptSession = useCallback((nextSession: Session) => {
    localStorage.setItem(SESSION_KEY, JSON.stringify(nextSession));
    adEventContext.current = { token: nextSession.token, generation: adEventContext.current.generation + 1 };
    setLoading(true);
    setSession(nextSession);
  }, []);

  const authenticate = async (email: string, password: string) => {
    setAuthBusy(true);
    setAuthError('');
    try {
      const nextSession = await signIn(email, password);
      acceptSession(nextSession);
    } catch (error) {
      setAuthError(error instanceof Error ? error.message : 'Não foi possível entrar.');
    } finally {
      setAuthBusy(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(SESSION_KEY);
    adEventContext.current = { token: null, generation: adEventContext.current.generation + 1 };
    Object.keys(sessionStorage).filter(key => key.startsWith('innova-live-ad-'))
      .forEach(key => sessionStorage.removeItem(key));
    setSession(null);
    setChannels([]);
    setPlaying(null);
    setVodPlaying(null);
    setVodDetail(null);
    setContinueWatching([]);
    setHomeAds({});
    setPartnerHeroAd(null);
    setFavoriteIds(new Set());
    setScreen('home');
  };

  if (!themeReady && (PAIRING_CODE || !session)) return <StatusScreen />;
  if (PAIRING_CODE) return <PairingActivationScreen code={PAIRING_CODE} theme={publicTheme} />;
  if (!session) return <LoginScreen busy={authBusy} error={authError} theme={publicTheme}
    takeover={loginAd} onAdImpression={ad => sendAdEvent(ad, 'impression')}
    onSubmit={authenticate} onPaired={acceptSession} />;
  if (loading) return <StatusScreen />;
  if (catalogError) return <StatusScreen error={catalogError} onRetry={() => void refreshCatalog()} />;
  if (playing) {
    return (
      <Suspense fallback={<StatusScreen />}>
        <PlayerScreen channel={playing} channels={channels} token={session.token}
          onChannelChange={setPlaying} onClose={() => setPlaying(null)} onAdEvent={sendAdEvent} />
      </Suspense>
    );
  }
  if (vodPlaying) return <Suspense fallback={<StatusScreen />}><VodPlayerScreen
    key={vodPlaying.id} item={vodPlaying} token={session.token} onClose={closeVod}
    onProgress={persistProgress} onAdEvent={sendAdEvent}
  /></Suspense>;

  const activeTheme = session.partner || publicTheme;
  return (
    <div className="app-shell" data-screen={screen} data-partner={activeTheme.slug} style={{ '--partner-accent': activeTheme.primary_color, '--partner-secondary': activeTheme.secondary_color } as CSSProperties}>
      <Sidebar screen={screen} session={session} onNavigate={next => {
        setVodDetail(null);
        setScreen(next);
      }} onLogout={logout} />
      {screen === 'partner' && activeTheme.features.length ? <PartnerHubScreen
        theme={activeTheme}
        heroAd={partnerHeroAd}
        channels={channels}
        movies={movies}
        series={series}
        onPlayChannel={setPlaying}
        onSelectTitle={selectHomeVod}
        onNavigate={next => setScreen(next)}
        onAdImpression={ad => sendAdEvent(ad, 'impression')}
        onAdSelect={selectAd}
      /> : screen === 'movies' || screen === 'series' ? <VodScreen
        key={screen}
        kind={screen === 'movies' ? 'movie' : 'series'}
        titles={screen === 'movies' ? movies : series}
        detail={vodDetail}
        detailLoading={vodDetailLoading}
        onSelect={title => void selectVod(title)}
        onCloseDetail={() => setVodDetail(null)}
        onPlay={setVodPlaying}
        favoriteIds={favoriteIds}
        onToggleFavorite={toggleFavorite}
      /> : <HomeScreen
        channels={channels} categories={categories} screen={screen} onPlay={setPlaying}
        featured={featured} featuredSettings={featuredSettings}
        onLoadSchedules={date => loadSchedules(date)}
        continueWatching={continueWatching} onResume={progress => void resumeVod(progress)}
        homeAds={homeAds} onAdImpression={ad => sendAdEvent(ad, 'impression')} onAdSelect={selectAd}
        movies={movies} series={series} onSelectVod={selectHomeVod} onNavigate={next => {
          setVodDetail(null);
          setScreen(next);
        }}
      />}
      {screen === 'partner' && homeAds.partner_external_feature && <aside className="tv-partner-external" data-testid="tv-partner-external-feature"><span>PUBLICIDADE</span><img src={homeAds.partner_external_feature.image} alt={homeAds.partner_external_feature.title} /></aside>}
      {screensaverAd && <div className="tv-screensaver" data-testid="tv-ad-screensaver"><img src={screensaverAd.image} alt={screensaverAd.title} /><span>PUBLICIDADE</span></div>}
    </div>
  );
}
